const gradients = {
    gradientMagic: "bg-gradient-magic",
    gradientTrend: "bg-gradient-trend",
    gradientNugget: "bg-gradient-nugget",
    gradientNew: "bg-gradient-new",
    gradientExplicit: "bg-gradient-explicit",
    gradientLabel: "bg-gradient-label",
    gradientGeneric: "bg-gradient-generic",
    gradientStats: "bg-gradient-stats",
    gradientFavorites: "bg-gradient-favorites",
    gradientDefault: "bg-gradient-default",
    gradientImport: "bg-gradient-import",
    gradientBanners: "bg-gradient-banners",
    gradientScribs: "bg-gradient-scribs",
    gradientDark: "bg-gradient-dark",
};

const gradientCompoundVariants = Object.entries(gradients).map(([variant, className]) => ({
    color: "gradient" as const,
    variant: variant as any,
    class: `!${className} ${variant === "gradientDark" ? "!text-white" : "!text-primary"} border-none`,
}));

export default defineAppConfig({
    ui: {
        icons: {
            eye: "i-heroicons-eye",
            eyeOff: "i-heroicons-eye-slash",
        },
        theme: {
            colors: {
                primary: "gray",
                secondary: "yellow",
                success: "green",
                error: "red",
                info: "blue",
                warning: "orange",
                neutral: "gray",
            },
        },
        alert: {
            slots: {
                root: "rounded-xs",
                title: "font-semibold text-base",
                description: "text-base",
            },
            compoundVariants: [
                {
                    color: "primary",
                    variant: "solid",
                    class: {
                        root: "ring-2 ring-primary bg-gradient-generic text-default",
                    },
                },
                {
                    color: "primary",
                    variant: "outline",
                    class: {
                        root: "ring-2 ring-primary text-default",
                    },
                },
                {
                    color: "secondary",
                    variant: "solid",
                    class: {
                        root: "ring-2 ring-primary bg-yellow-400 text-default",
                    },
                },
                {
                    color: "neutral",
                    variant: "solid",
                    class: {
                        root: "ring-0 bg-gray-50 text-default",
                    },
                },
                {
                    color: "info",
                    variant: "solid",
                    class: {
                        root: "ring-0 bg-info-50 text-default",
                    },
                },
                {
                    color: "success",
                    variant: "solid",
                    class: {
                        root: "ring-0 bg-success-50 text-default",
                    },
                },
                {
                    color: "error",
                    variant: "solid",
                    class: {
                        root: "ring-0 bg-error-50 text-default",
                    },
                },
                {
                    color: "warning",
                    variant: "solid",
                    class: {
                        root: "ring-0 bg-warning-50 text-default",
                    },
                },
            ],
        },
        avatar: {
            slots: {
                root: "rounded-xs",
            },
            defaultVariants: {
                size: "lg",
            },
        },
        badge: {
            slots: {
                base: "font-mono font-semibold !text-primary",
            },
            variants: {
                color: {
                    primary: "!ring-2 border-primary rounded-sm !bg-gray-200",
                    secondary: "!ring-2 border-dashed rounded-sm !bg-white",
                    neutral: "!ring-2 border-gray-200 rounded-sm !bg-white",
                    gradient: "",
                },
                variant: {
                    solid: "",
                    outline: "",
                    soft: "",
                    subtle: "",
                    ...gradients,
                },
                size: {
                    xs: {
                        base: "px-2 py-1 text-xs leading-none",
                    },
                    sm: {
                        base: "px-2 py-1 lg:py-1.75 text-sm leading-none",
                    },
                    md: {
                        base: "px-4 py-2.75 text-sm leading-none",
                    },
                },
            },
            compoundVariants: [
                ...gradientCompoundVariants,
            ],
        },
        banner: {
            slots: {
                title: "font-mono font-semibold text-primary",
            },
        },
        button: {
            slots: {
                base: "rounded-sm ring-inset cursor-pointer hover:!bg-gray-100",
            },
            defaultVariants: {
                color: "neutral",
                variant: "outline",
            },
            variants: {
                color: {
                    primary: "font-serif italic",
                    secondary: "font-serif italic",
                    error: "font-serif italic",
                },
                size: {
                    sm: {
                        base: "px-4 py-2 text-sm leading-none",
                    },
                    md: {
                        base: "px-4 py-3 text-base leading-none",
                    },
                    lg: {
                        base: "px-3 py-3 md:px-4 md:py-4 text-base leading-none",
                    },
                    xl: {
                        base: "px-4 py-4 md:px-8 md:py-7 text-xl leading-none",
                    },
                },
            },
            compoundVariants: [
                {
                    color: "primary",
                    variant: "solid",
                    class: "font-serif font-extrabold italic leading-none text-yellow-400 ring-2 ring-primary bg-gray-900 hover:bg-yellow-400 hover:text-primary disabled:bg-gray-200 disabled:ring-gray-300 disabled:text-gray-300",
                },
                {
                    color: "secondary",
                    variant: "solid",
                    class: "font-serif font-extrabold italic leading-none text-primary ring-2 ring-primary bg-yellow-400 hover:bg-yellow-50 disabled:bg-yellow-400",
                },
                {
                    color: "neutral",
                    variant: "outline",
                    class: "font-serif font-semibold italic leading-none text-primary ring-2 ring-gray-300 bg-white hover:bg-gray-200",
                },
                {
                    color: "neutral",
                    variant: "link",
                    size: "sm",
                    class: "font-sans font-bold text-primary text-sm",
                },
                {
                    color: "neutral",
                    variant: "link",
                    size: "md",
                    class: "font-sans font-bold text-gray-400 text-md",
                },
                {
                    color: "error",
                    variant: "solid",
                    class: "font-serif font-extrabold italic leading-none text-red-950 ring-2 ring-red-950 bg-red-400 hover:bg-red-950 hover:text-red-100 disabled:bg-gray-200 disabled:text-gray-300",
                },
                {
                    color: "success",
                    variant: "solid",
                    class: "font-serif font-extrabold italic leading-none text-green-950 ring-2 ring-green-950 bg-green-400 hover:bg-green-50 disabled:bg-green-400",
                },
                {
                    color: "neutral",
                    variant: "soft",
                    class: "bg-white text-gray-400 text-sm",
                },
            ],
        },
        card: {
            slots: {
                root: "rounded-xs border-primary shadow-none",
                body: "p-6",
            },
        },
        checkbox: {
            slots: {
                base: "rounded-xs ring-gray-300",
                icon: "text-yellow-400 size-3",
                label: "text-primary font-normal",
                description: "text-gray-300",
            },
        },
        container: {
            base: "px-4",
        },
        drawer: {
            slots: {
                overlay: "bg-primary/37",
                container: "p-6",
            },
        },
        header: {
            slots: {
                toggle: "md:hidden",
            },
        },
        input: {
            slots: {
                base: "!py-3 rounded-xs font-sans text-base",
            },
        },
        inputMenu: {
            slots: {
                base: "rounded-xs font-sans text-base !py-3",
                item: "font-sans",
            },
        },
        formField: {
            slots: {
                label: "font-semibold",
            },
        },
        link: {
            base: "!text-gray-400 hover:!text-gray-700",
        },
        modal: {
            slots: {
                header: "sm:p-12 sm:pb-6 border-b-0",
                body: "sm:px-12 sm:pb-6",
                footer: "sm:p-12 sm:pt-6",
                overlay: "!bg-primary/37",
                content: "rounded-lg divide-none !ring-0",
            },
        },
        navigationMenu: {
            slots: {
                link: "data-active:font-semibold !text-gray-900",
            },
        },
        pageBody: {
            base: "mt-16",
        },
        pageHeader: {
            slots: {
                root: "border-dashed",
            },
        },
        pagination: {
            slots: {
                item: "data-[selected=true]:bg-yellow-400 data-[selected=true]:text-primary font-sans font-normal not-italic py-2 ring-0 data-selected:ring-2",
                first: "py-2 ring-0",
                prev: "py-2 ring-0",
                next: "py-2 ring-0",
                last: "py-2 ring-0",
            },
        },
        pricingPlan: {},
        radioGroup: {
            slots: {
                base: "rounded-full",
                indicator: "after:bg-yellow-400",
                label: "text-primary font-normal",
                description: "text-gray-300",
            },
        },
        select: {
            slots: {
                base: "rounded-xs font-sans text-base !py-3",
                placeholder: "text-gray-400",
                item: "font-sans",
            },
        },
        selectMenu: {
            slots: {
                base: "rounded-xs font-sans text-base !py-3",
                placeholder: "text-gray-400",
                item: "font-sans",
                value: "pl-1",
            },
        },
        separator: {
            slots: {
                border: "!border-dashed border-gray-500",
            },
        },
        switch: {
            slots: {
                base: "rounded-xs ring-1 ring-primary data-[state=unchecked]:!bg-white data-[state=checked]:!bg-yellow-400",
                label: "text-primary font-normal",
                description: "text-gray-300",
                thumb: "rounded-xs bg-primary data-[state=checked]:bg-primary",
                icon: "group-data-[state=unchecked]:text-white group-data-[state=checked]:!text-yellow-400 transition-none",
            },
            variants: {
                color: {
                    neutral: {
                        base: "data-[state=checked]:!bg-white",
                    },
                },
            },
        },
        tabs: {
            slots: {
                label: "font-sans text-base",
                list: "border-gray-200 md:!mb-8",
                indicator: "!h-[2px]",
            },
        },
        textarea: {
            slots: {
                base: "!py-3",
            },
        },
        toast: {
            slots: {
                root: "bg-gradient-generic",
            },
        },
    },
});
